import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { formatCurrency } from '../../../utils/formatters';

// Define colors based on brand palette
const primaryButtonBg = 'bg-brand-teal';
const primaryButtonHoverBg = 'hover:bg-brand-teal-dark';
const primaryButtonText = 'text-white';

const CartTotal = ({ 
  totalAmount,
  depositAmount = 0,
  hasShippingItems,
  isCheckingOut,
  updating,
  checkoutError,
  handleCheckout,
  primaryCurrency = 'USD',
  hasRentalItemsInGroup
}) => {
  // Debug received props
  useEffect(() => {
    console.log('CartTotal received props:', {
      totalAmount,
      hasShippingItems,
      depositAmount,
      primaryCurrency
    });
  }, [totalAmount, hasShippingItems, depositAmount, primaryCurrency]);

  // Calculate platform fee and tax
  const platformFee = totalAmount * 0.1; // 10% platform fee
  const tax = (totalAmount + platformFee) * 0.08; // 8% tax
  
  // Calculate total without shipping (which will be calculated at checkout)
  const totalWithoutShipping = totalAmount + platformFee + tax;

  // Ensure we have a valid currency
  const currency = primaryCurrency || 'USD';

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Order Summary</h2>
      
      <div className="space-y-3">
        <div className="flex justify-between">
          <span className="text-gray-600">Subtotal:</span>
          <span className="font-medium">{formatCurrency(totalAmount, currency)}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Platform Fee:</span>
          <span className="font-medium">{formatCurrency(platformFee, currency)}</span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Tax:</span>
          <span className="font-medium">{formatCurrency(tax, currency)}</span>
        </div>
        
        {hasShippingItems && (
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Shipping:</span>
            <span className="text-gray-600 text-sm">Calculated at checkout</span>
          </div>
        )}
        
        {depositAmount > 0 && (
          <div className="flex justify-between border-t border-gray-200 pt-3 mt-3">
            <span className="text-gray-600">Security Deposit:</span>
            <span className="font-medium">{formatCurrency(depositAmount, currency)}</span>
          </div>
        )}
        
        <div className="flex justify-between border-t border-gray-200 pt-3 mt-3">
          <span className="font-semibold">Total:</span>
          <span className="font-bold text-lg">
            {formatCurrency(totalWithoutShipping, currency)}
            {/* {hasShippingItems && ' + shipping'} */}
          </span>
        </div>
        
        {depositAmount > 0 && (
          <div className="flex justify-between">
            <span className="font-semibold">Total + Deposit:</span>
            <span className="font-bold text-lg">
              {formatCurrency(totalWithoutShipping + depositAmount, currency)}
              {/* {hasShippingItems && ' + shipping'} */}
            </span>
          </div>
        )}
        
        {checkoutError && (
          <div className="text-red-500 text-sm mt-2">
            {checkoutError}
          </div>
        )}
        
        <div className="flex justify-center mt-6">
          <button
            type="button"
            onClick={handleCheckout}
            disabled={isCheckingOut || updating || totalAmount === 0}
            className={`py-3 px-8 ${primaryButtonBg} ${primaryButtonHoverBg} ${primaryButtonText} rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-teal transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isCheckingOut || updating ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </span>
            ) : (
              'Proceed to Checkout'
            )}
          </button>
        </div>
        
        {hasRentalItemsInGroup && (
          <p className="text-xs text-gray-500 mt-4 text-center">
            * Security deposits are fully refundable when items are returned in their original condition.
          </p>
        )}
      </div>
    </div>
  );
};

export default CartTotal; 